import * as React from 'react';
import { AuthApiError } from '../errors.js';
import { useAuthContext } from './auth-provider.js';
/**
 * Reads the current session from `/auth/me` and caches it on the AuthProvider
 * so other guards (`<RequireGroup>`, `<RequireAuthenticated>`) see the same
 * value without re-fetching.
 *
 * Returns `user: null` (rather than throwing) when the server says 401 — that's
 * the legitimate "no session" case and screens like `<LoginForm>` need to
 * render normally.
 */
export function useAuthMe() {
    const { client, token, user, setUser } = useAuthContext();
    const [loading, setLoading] = React.useState(token !== null && user === null);
    const [error, setError] = React.useState(null);
    const load = React.useCallback(async () => {
        if (token === null || token.length === 0) {
            setUser(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const next = await client.getMe();
            setUser(next);
        }
        catch (err) {
            if (err instanceof AuthApiError && err.httpStatus === 401) {
                // Treat 401 as "no session" rather than an error — the UI typically
                // wants to render the login screen, not an error banner.
                setUser(null);
            }
            else if (err instanceof Error) {
                setError(err);
            }
            else {
                setError(new Error(String(err)));
            }
        }
        finally {
            setLoading(false);
        }
    }, [client, token, setUser]);
    React.useEffect(() => {
        void load();
    }, [load]);
    return { user, loading, error, reload: load };
}
//# sourceMappingURL=use-auth-me.js.map